import { useCallback, useEffect, useMemo, useState } from 'react';
import { ChannelLayout } from 'core/models/channel';
import { MultiplexedChannel } from 'infrastructure/t4/ChannelResolver/MultiplexedChannel';
import { MultiChannelLatencyMeasure } from 'services/latency/MultiChannelLatencyMeasure';
import { LatencyData, LatencyMeasurePoint } from 'core/models/latency';

export function useLatencyMeasure(layout: ChannelLayout | undefined) {
    const latencyList = useMemo(() => {
        const list = new Map<string, MultiChannelLatencyMeasure>();
        layout?.cameras.forEach((value, key) => {
            list.set(key, new MultiChannelLatencyMeasure(key, value as MultiplexedChannel));
        });
        return list;
    }, [layout]);

    return latencyList;
}

export function useUpdateLatencyMeasures(list: Map<string, MultiChannelLatencyMeasure>, msec: number) {
    const [currentMeasures, setCurrentMeasures] = useState<Array<{ tag: string, measure: (LatencyData | undefined)[] }>>();
    useEffect(() => {
        if (!list) return;
        const interval = setInterval(async () => {
            const currentData = Array<{ tag: string, measure: (LatencyData | undefined)[] }>();
            list.forEach((measure, tag) => {
                list?.get(tag)?.update();
                currentData.push({ tag, measure: measure.lastMeasurements });
            });
            setCurrentMeasures(currentData);
        }, msec);
        return () => clearInterval(interval);
    }, [list, msec]);

    return currentMeasures;
}

export function useLatencyMeasurePoints(list: Map<string, MultiChannelLatencyMeasure>) {
    const [points, setPoints] = useState<LatencyMeasurePoint>(() => []);

    const addPoint = useCallback(() => {
        setPoints(prevPoints =>
            [
                {
                    time: new Date(),
                    cameras: [...list].map((camera) => ({ tag: camera[0], channels: camera[1].averageMeasurements.map((m, channel) => ({ channel, measurement: m })) })),
                },
                ...prevPoints,
            ]);
    }, [list]);

    const clearPoints = useCallback(() => {
        list.forEach((measure) => {
            measure.reset();
        });
        setPoints([]);
    }, [list]);

    return { points, addPoint, clearPoints };
}
